import { EVSE, type EVSESnapshot } from './EVSE';
import { InvariantViolation, UnknownConnectorError, UnknownEvseError } from './errors';
import type { ConnectorId, EvseId } from './types';

export interface TopologySnapshot {
  readonly cpMaxPowerW: number;
  readonly evses: readonly EVSESnapshot[];
}

/** OCPP 2.0.1 wire-level addressing: (evseId, localConnectorId) starting at 1. */
export interface LocalAddress {
  readonly evseId: EvseId;
  readonly localConnectorId: number;
}

export class Topology {
  readonly cpMaxPowerW: number;
  readonly evses: readonly EVSE[];

  readonly #globalToLocal = new Map<ConnectorId, LocalAddress>();
  readonly #localToGlobal = new Map<string, ConnectorId>();
  readonly #evseById = new Map<EvseId, EVSE>();

  constructor(snapshot: TopologySnapshot) {
    if (snapshot.evses.length === 0) {
      throw new InvariantViolation('Topology', 'topology.evses cannot be empty');
    }
    if (!(snapshot.cpMaxPowerW > 0)) {
      throw new InvariantViolation('Topology', 'cpMaxPowerW > 0');
    }

    const evses: EVSE[] = [];
    const seenEvseIds = new Set<EvseId>();
    const seenConnectorIds = new Set<ConnectorId>();

    for (const evseSnap of snapshot.evses) {
      if (seenEvseIds.has(evseSnap.id)) {
        throw new InvariantViolation(
          'Topology',
          'evses[].id is unique within station',
          `duplicate evseId=${evseSnap.id}`,
        );
      }
      seenEvseIds.add(evseSnap.id);

      const evse = new EVSE(evseSnap);
      evses.push(evse);
      this.#evseById.set(evse.id, evse);

      for (let i = 0; i < evse.connectorIds.length; i++) {
        const globalId = evse.connectorIds[i] as ConnectorId;
        if (seenConnectorIds.has(globalId)) {
          throw new InvariantViolation(
            'Topology',
            'All connectorIds across all EVSEs are unique within station',
            `duplicate connectorId=${globalId}`,
          );
        }
        seenConnectorIds.add(globalId);

        const localConnectorId = i + 1;
        this.#globalToLocal.set(globalId, { evseId: evse.id, localConnectorId });
        this.#localToGlobal.set(localKey(evse.id, localConnectorId), globalId);
      }
    }

    this.cpMaxPowerW = snapshot.cpMaxPowerW;
    this.evses = evses;
  }

  get connectorIds(): readonly ConnectorId[] {
    return [...this.#globalToLocal.keys()];
  }

  hasConnector(connectorId: ConnectorId): boolean {
    return this.#globalToLocal.has(connectorId);
  }

  getEvse(evseId: EvseId): EVSE {
    const evse = this.#evseById.get(evseId);
    if (!evse) throw new UnknownEvseError(evseId);
    return evse;
  }

  /** Translate a global ConnectorId to the OCPP 2.0.1 (evseId, localConnectorId) tuple. */
  getLocalIdFor(connectorId: ConnectorId): LocalAddress {
    const local = this.#globalToLocal.get(connectorId);
    if (!local) throw new UnknownConnectorError(connectorId);
    return local;
  }

  /** Translate an OCPP 2.0.1 (evseId, localConnectorId) tuple back to a global ConnectorId. */
  getGlobalIdFor(evseId: EvseId, localConnectorId: number): ConnectorId {
    const global = this.#localToGlobal.get(localKey(evseId, localConnectorId));
    if (global === undefined) {
      throw new UnknownConnectorError(localConnectorId);
    }
    return global;
  }

  toSnapshot(): TopologySnapshot {
    return {
      cpMaxPowerW: this.cpMaxPowerW,
      evses: this.evses.map((e) => e.toSnapshot()),
    };
  }
}

function localKey(evseId: EvseId, localConnectorId: number): string {
  return `${evseId}:${localConnectorId}`;
}
